import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormGroup } from '@mui/material'

import { FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent, MedBGContainer, FlexColumn, WhiteBGContainer } from '../styles/Containers'
import { ErrorMessage, SuccessMessage } from '../common/UserMessage'
import { FormControlLabel, Checkbox } from '../styles/Checkbox'
import BodyText, { BoldFormTitle, Subheading, ErrorText } from '../styles/Text'
import Button, { SecondaryButton } from '../common/Button'
import axios from '../../dataClient/dataClient'

const ChecklistGroup = styled(FormGroup)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const CheckboxLabel = styled(FormControlLabel)`
  .MuiTypography-root {
    word-break: break-word;
  }
`

const schema = yup.object().shape({
  emails: yup.array().min(1, 'At least one email must be selected').required('At least one email must be selected')
})

function ResendDownloadLinkButton({ pirId, disabled = false, requestor, additionalRecipients }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [display, setDisplay] = useState('form')

  const { handleSubmit, register, formState, reset } = useForm({ resolver: yupResolver(schema), defaultValues: { emails: [] } })

  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const onError = (errors, e) => console.error('Error', errors, e)

  const handleOpen = () => setIsVisible(true)
  const handleClose = () => {
    setError(false)
    setIsVisible(false)
    setIsLoading(false)
    reset()
    setDisplay('form')
  }

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    axios
      .post(`/email/pir-download-link/${pirId}`, { recipients: data.emails })
      .then(() => setDisplay('success'))
      .catch(e => {
        console.error('failed to resend download link', e)
        setError(true)
        setIsLoading(false)
      })
  }

  const formError = !!formState.errors.emails
  const helperText = formState.errors?.emails?.message

  return (
    <>
      <SecondaryButton disabled={disabled} onClick={handleOpen}>
        Resend Download Link
      </SecondaryButton>
      <StyledDialog fullWidth open={isVisible} onClose={handleClose} keepMounted={false}>
        <StyledDialogContent>
          {display === 'form' && (
            <FormProvider>
              <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
                <BoldFormTitle>Resend Download Link</BoldFormTitle>
                <BodyText>Select the recipients you would like to resend the link to:</BodyText>
                <ChecklistGroup>
                  <MedBGContainer padding="13px 20px 5px">
                    <Subheading>Requestor</Subheading>
                    <CheckboxLabel
                      {...register('emails')}
                      control={<Checkbox />}
                      label={requestor.label}
                      value={requestor.value}
                      disabled={isLoading}
                    />
                  </MedBGContainer>
                  {additionalRecipients && (
                    <MedBGContainer padding="13px 20px 5px">
                      <Subheading>Additional Recipients</Subheading>
                      <FlexColumn gap="0">
                        {additionalRecipients.map((email, i) => (
                          <CheckboxLabel
                            {...register('emails')}
                            key={i}
                            control={<Checkbox />}
                            label={email.label}
                            value={email.value}
                            disabled={isLoading}
                          />
                        ))}
                      </FlexColumn>
                    </MedBGContainer>
                  )}
                  {formError && <ErrorText role="alert">{helperText}</ErrorText>}
                </ChecklistGroup>
                {error && <ErrorMessage />}
                <FlexEndContainer>
                  <SecondaryButton onClick={handleClose} disabled={isLoading}>
                    Cancel
                  </SecondaryButton>
                  <Button type="submit" loading={isLoading}>
                    Send Link
                  </Button>
                </FlexEndContainer>
              </PaddedForm>
            </FormProvider>
          )}
          {display === 'success' && (
            <WhiteBGContainer padding="30px">
              <FlexColumn>
                <BoldFormTitle>Resend Download Link</BoldFormTitle>
                <SuccessMessage>Download link successfully resent.</SuccessMessage>
                <FlexEndContainer>
                  <SecondaryButton onClick={handleClose}>Close</SecondaryButton>
                </FlexEndContainer>
              </FlexColumn>
            </WhiteBGContainer>
          )}
        </StyledDialogContent>
      </StyledDialog>
    </>
  )
}

ResendDownloadLinkButton.propTypes = {
  pirId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  requestor: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  }),
  additionalRecipients: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  )
}

export default ResendDownloadLinkButton
